import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import type { CalendarEvent } from '../utils/calendar-helpers';
import type { GetCaregiverCalendarResponse } from '../types/api.types';
import { transformCalendarToEvents } from '../utils/api-transforms';

export function useCaregiverCalendar(
  caregiverIds: string[],
  viewMode: 'day' | 'week' | 'month',
  currentDate: Date
) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCalendars = async () => {
      if (caregiverIds.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { homeNursingService } = await import('../../api/homeNursingClient');

        console.log('=== CALLING GET /public/caregivers/:id/calendar (x' + caregiverIds.length + ') ===');
        console.log('Caregiver IDs:', caregiverIds);
        console.log('Params:', {
          view: viewMode,
          date: format(currentDate, 'yyyy-MM-dd'),
        });

        // Fetch calendar for all selected caregivers in parallel
        const calendarPromises = caregiverIds.map((id, index) => {
          console.log(`[${index + 1}/${caregiverIds.length}] Fetching calendar for caregiver:`, id);
          return homeNursingService.getCaregiverCalendar(id, {
            view: viewMode,
            date: format(currentDate, 'yyyy-MM-dd'),
          });
        });

        const calendars = await Promise.all(calendarPromises);

        console.log('=== CALENDAR API RESPONSES ===');
        calendars.forEach((calendar: GetCaregiverCalendarResponse, index) => {
          console.log(`\n========== CAREGIVER ${index + 1} CALENDAR ==========`);
          console.log('Full calendar response:', JSON.stringify(calendar, null, 2));
        });

        // Transform all calendars to events
        const allEvents = calendars.flatMap((calendar: GetCaregiverCalendarResponse) => {
          return transformCalendarToEvents(calendar.data, calendar.data.caregiverId);
        });

        console.log('=== EVENTS TRANSFORMED ===');
        console.log('Total events:', allEvents.length);
        console.log('First event:', allEvents[0]);

        // Count leave events
        const leaveEvents = allEvents.filter(e => e.type === 'leave');
        console.log('Leave events:', leaveEvents.length);
        console.log('Leave events detail:', leaveEvents);

        setEvents(allEvents);
        setError(null);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to fetch calendars:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendars();
  }, [caregiverIds, viewMode, currentDate]);

  return { events, loading, error };
}
